import React, { useReducer } from 'react';
import styled from '@emotion/styled';
import Tabs, { Tab } from '@influitive/secret-garden/lib/tabs';
import AsyncButton from '@influitive/secret-garden/lib/async-button';
import { themeGet } from 'styled-system';
import * as R from 'ramda';

import DetailsTabContent from './components/details-tab';
import BadgesTabContent from './components/badges-tab';
import Context from './context';
import * as actions from './actions';
import reducer from './reducers';
import { buildInitialState, saveButtonStatusSelector, buildErrorMessage } from './util';

const SaveButton = styled(AsyncButton)`
  margin-left: ${themeGet('spacing.3')}px;
`;

const StyledTabs = styled(Tabs)`
  .tab-content {
    padding-top: ${themeGet('spacing.4')}px;
  }

  .nav-tabs {
    border: none;
    font-size: ${themeGet('fonts.size.3')}px;
  }
`;

const Wrapper = styled.div`
  font-size: ${themeGet('fonts.size.2')}px;
`;

const ErrorMessage = styled(({ errors, ...rest }) => errors ? <div {...rest}>{buildErrorMessage(errors)}</div> : null)`
  color: ${themeGet('colors.error.1')};
`;

export default R.pipe(
  ({ level, existingLevels, allBadges, activeTab }) => {
    const [state, dispatch] = useReducer(reducer, buildInitialState(level, existingLevels, allBadges));

    const handleClick = () => {
      if (state.isSaving) return;

      dispatch(actions.setIsSaving(true));
      actions.saveForm(state, dispatch);
    };

    const saveButtonStatus = saveButtonStatusSelector(state);

    return {
      allBadges,
      saveButtonStatus,
      handleClick,
      dispatch,
      activeTab,
      ...state
    };
  },
  ({
    form,
    errors,
    levels,
    allBadges,
    selectedBadges,
    saveButtonStatus,
    handleClick,
    activeTab,
    dispatch
  }) =>
    <Context.Provider value={{ dispatch }}>
      <Wrapper key='wrapper'>
        <ErrorMessage errors={errors.common} />
        <StyledTabs defaultActiveKey={activeTab} id="level-form-tabs" data-qa="level-form-tabs">
          <Tab eventKey="details" title="Details" data-qa="details-tab">
            <DetailsTabContent
              level={form}
              levels={levels}
              errors={errors} />
          </Tab>
          <Tab eventKey="badges" title="Badges" disabled={R.not(form['allows-badges'])} data-qa="badges-tab">
            <BadgesTabContent
              allBadges={allBadges}
              selectedBadges={selectedBadges} />
          </Tab>
        </StyledTabs>

        <SaveButton
          data-qa="add-new-level"
          text={[(form.id ? 'Save level' : 'Add new level'), 'Saving level...', 'Level saved', 'Error']}
          size="large"
          status={saveButtonStatus}
          onClick={handleClick} />
      </Wrapper>
    </Context.Provider>
);
